import React, {
  useCallback, useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { Header, Message, Confirm } from 'semantic-ui-react';
import { setPage } from '../data/page/actions';
import VRAPendingList from '../views/VRAPending/VRAPendingList';
import VRAPendingDetails from '../views/VRAPending/VRAPendingDetails';
import InvalidatePendingForm from '../views/VRAPending/InvalidatePendingForm';
import PendingVraNotesDialog from '../views/VRAPending/PendingVraNotesDialog';
import ConfirmGuaranteeExtension, { GRACE_PERIOD_DAYS } from '../views/VRAPending/ConfirmGuaranteeExtension';
import ReturnInvoiceEdit from '../views/ReturnInvoices/Edit';
import pendingReturnInvoicesQuery from '../graphql/pendingReturnInvoicesQuery';
import pendingVRAMutation from '../graphql/pendingVRAMutation';
import pendingVraNotesQuery from '../graphql/pendingVraNotesQuery';
import addPendingVraNoteMutation from '../graphql/addPendingVraNoteMutation';
import updateGuaranteePurchaseMutation from '../graphql/updateGuaranteePurchaseMutation';
import guaranteeExtensionEligibilityQuery from '../graphql/guaranteeExtensionEligibilityQuery';
import updateReturnInvoiceMutation from '../graphql/updateReturnInvoiceMutation';
import VRAPendingSearch from '../views/VRAPending/VRAPendingSearch';
import client from '../apollo';
import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import { isExpired } from '../utilities/dateUtils';
import {
  PENDING_VRA_NOTES_ACTION,
  PENDING_VRA_NOTES_LOAD_ERROR,
  PENDING_VRA_NOTE_SAVE_ERROR,
} from '../constants/pendingVraNotes';
import { allowAccess } from '../utilities/user';
import ACCESS from '../constants/access';

const VRA_ACTION_MESSAGES = {
  ALLOW: {
    success: 'Successfully Approved the VRA pending',
    error: 'Failed to process the Allow action for the Pending VRA. Please reach out to the DealShield team.',
  },
  REJECT: {
    success: 'Successfully Invalidated the VRA Pending.',
    error: 'Failed to process the Invalidate action for the Pending VRA. Please reach out to the DealShield team.',
  },
  EXPIRED: {
    error: 'This action cannot be completed because the return date has expired',
  },
};

export const calculateExtensionDays = expirationDate => moment.utc()
  .startOf('day')
  .add(GRACE_PERIOD_DAYS, 'days')
  .diff(moment.utc(expirationDate).startOf('day'), 'days');

const buildGuaranteePurchaseExtensionInput = (pendingVRA, extensionDays) => {
  const guaranteePurchase = pendingVRA?.guaranteePurchase ?? {};
  const guaranteeOffer = guaranteePurchase.guaranteeOffer ?? {};
  const vehicleInformation = guaranteeOffer.vehicleInformation ?? {};
  const auctionInformation = guaranteeOffer.auctionInformation ?? {};

  return {
    id: parseInt(guaranteePurchase.id, 10),
    dealerNumber: parseInt(auctionInformation.dealerNumber, 10),
    auctionAccessNumber: parseInt(auctionInformation.auctionAccessNumber, 10),
    buyFee: parseFloat(auctionInformation.buyFee || 0),
    vehiclePurchasePrice: parseFloat(auctionInformation.vehiclePurchasePrice),
    year: parseInt(vehicleInformation.year, 10),
    make: vehicleInformation.make,
    model: vehicleInformation.model || '',
    odometerReading: parseInt(vehicleInformation.odometerReading, 10),
    partner: guaranteePurchase.partner || '',
    kind: guaranteePurchase.kind,
    milesSelected: parseInt(guaranteeOffer.milesSelected, 10),
    daysSelected: parseInt(guaranteeOffer.daysSelected, 10),
    transportReimbursement: parseFloat(guaranteeOffer.transportReimbursement),
    emails: guaranteeOffer.emails,
    guaranteedAt: guaranteePurchase.guaranteedAt,
    guaranteePrice: parseFloat(guaranteeOffer.guaranteePrice || 0),
    extensionReason: '',
    extensionDays,
  };
};

export const VRAPending = props => {
  const {
    data: { pendingReturnInvoices, refetch },
    pendingVRAMutation: submitPendingVRAMutation,
    addPendingVraNoteMutation: submitAddPendingVraNote,
    updateGuaranteePurchaseMutation: submitUpdateGuaranteePurchaseMutation,
    setPageAction,
    user,
  } = props;

  // A pending_vra_viewer may open the list and the details; Edit, Allow and Invalidate are for the editor.
  // The backend refuses them as well, so this only keeps the screen honest.
  const canEdit = allowAccess(user, ACCESS.VRA_PENDING_EDIT);

  const [selectedVRAPending, setSelectedVRAPending] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isExtensionConfirmOpen, setIsExtensionConfirmOpen] = useState(false);
  const [extensionDays, setExtensionDays] = useState(null);
  const [isExtendingGuarantee, setIsExtendingGuarantee] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [error, setError] = useState(null);
  const [detailsError, setDetailsError] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isInvalidateModalOpen, setIsInvalidateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);
  const [isNoteSubmitting, setIsNoteSubmitting] = useState(false);
  const [noteSubmitError, setNoteSubmitError] = useState(null);

  const onSearchSubmitHandler = useCallback(event => {
    const { dealerNumber, vin, vra } = event.target;
    refetch({
      dealerNumber: dealerNumber.value.trim() || null,
      vin: vin.value.trim() || null,
      vra: vra.value.trim() || null,
    });
    setPageAction({});
  }, [refetch, setPageAction]);

  const resetVRASelection = () => {
    setIsDetailsModalOpen(false);
    setIsConfirmModalOpen(false);
    setIsExtensionConfirmOpen(false);
    setIsInvalidateModalOpen(false);
    setIsEditModalOpen(false);
    setIsNotesModalOpen(false);
    setNotes([]);
    setNotesError(null);
    setNoteSubmitError(null);
    setExtensionDays(null);
    setDetailsError(null);
    setSelectedVRAPending(null);
  };

  const submitPendingVRAAction = (pendingVRA, mutationAction, extraInput = {}) => {
    const { success: successMessage, error: errorMessage } = VRA_ACTION_MESSAGES[mutationAction];
    const mutationPromise = submitPendingVRAMutation({
      variables: {
        input: {
          pendingVra: { vraId: pendingVRA.id, action: mutationAction, ...extraInput },
        },
      },
    });
    mutationPromise
      .then(({ data: { pendingVra } }) => {
        const { success } = pendingVra;
        refetch();
        if (success) {
          setFlashMessage(successMessage);
          setError(null);
        } else {
          setFlashMessage(null);
          setError(errorMessage);
        }
      })
      .catch(() => {
        resetVRASelection();
        setFlashMessage(null);
        setError(errorMessage);
      });
  };

  const onConfirmModalCancelHandler = () => {
    setIsConfirmModalOpen(false);
  };

  const onConfirmModalConfirmHandler = () => {
    submitPendingVRAAction(selectedVRAPending, 'ALLOW');
    resetVRASelection();
  };

  const onInvalidateModalCancelHandler = () => {
    setIsInvalidateModalOpen(false);
  };

  const onInvalidateModalSubmitHandler = ({ reason }) => {
    submitPendingVRAAction(selectedVRAPending, 'REJECT', { reason });
    resetVRASelection();
  };

  const loadNotes = async pendingVRA => {
    setNotes([]);
    setNotesError(null);
    setNoteSubmitError(null);
    setNotesLoading(true);
    try {
      const { data } = await client.query({
        query: pendingVraNotesQuery,
        variables: { vraId: String(pendingVRA.id) },
        fetchPolicy: 'network-only',
      });
      setNotes(data?.pendingVraNotes?.notes ?? []);
    } catch (e) {
      // Name the server's reason when it gave one (e.g. "No VRA found for id '2'"); the generic text hides it.
      const reason = e?.graphQLErrors?.[0]?.message;
      setNotesError(reason ? `${PENDING_VRA_NOTES_LOAD_ERROR} (${reason})` : PENDING_VRA_NOTES_LOAD_ERROR);
    }
    setNotesLoading(false);
  };

  const onActionHandler = (action, pendingVRA) => {
    if (!action || !pendingVRA) return;
    // The details and the notes can be read by a viewer; every other action is the editor's.
    if (action !== 'view' && action !== PENDING_VRA_NOTES_ACTION && !canEdit) return;
    switch (action) {
      case 'view':
        setSelectedVRAPending(pendingVRA);
        setIsDetailsModalOpen(true);
        loadNotes(pendingVRA);
        break;
      case 'invalidate':
        setSelectedVRAPending(pendingVRA);
        setIsInvalidateModalOpen(true);
        break;
      case 'edit':
        setSelectedVRAPending(pendingVRA);
        setIsEditModalOpen(true);
        break;
      case PENDING_VRA_NOTES_ACTION:
        setSelectedVRAPending(pendingVRA);
        setIsNotesModalOpen(true);
        loadNotes(pendingVRA);
        break;
      default:
        break;
    }
  };

  const onNoteSubmitHandler = async({ note }) => {
    setIsNoteSubmitting(true);
    setNoteSubmitError(null);
    try {
      const { data } = await submitAddPendingVraNote({
        variables: { input: { vraId: String(selectedVRAPending.id), note } },
      });
      const { success, errors, pendingVraNotes } = data.addPendingVraNote;
      setIsNoteSubmitting(false);
      if (success) {
        setNotes(pendingVraNotes?.notes ?? []);
        return { success: true };
      }
      setNoteSubmitError((errors && errors.join(' ')) || PENDING_VRA_NOTE_SAVE_ERROR);
      return { success: false };
    } catch (e) {
      setIsNoteSubmitting(false);
      setNoteSubmitError(PENDING_VRA_NOTE_SAVE_ERROR);
      return { success: false };
    }
  };

  const onEditCloseHandler = () => {
    resetVRASelection();
  };

  const onEditSubmitHandler = values => {
    setIsEditSubmitting(true);
    const { updateReturnInvoiceMutation: submitUpdateReturnInvoiceMutation } = props;
    submitUpdateReturnInvoiceMutation({
      variables: { input: { returnInvoice: { ...values } } },
    })
      .then(({ data: { updateReturnInvoice } }) => {
        const { success, errors } = updateReturnInvoice;
        setIsEditSubmitting(false);
        if (success) {
          refetch();
          resetVRASelection();
          setFlashMessage('Successfully Edited VRA Pending');
          setError(null);
        } else {
          resetVRASelection();
          setFlashMessage(null);
          setError(errors);
        }
      })
      .catch(() => {
        setIsEditSubmitting(false);
        resetVRASelection();
        setFlashMessage(null);
        setError('Error updating VRA pending');
      });
  };

  const allowPendingVRA = () => {
    if (!selectedVRAPending) return;
    if (isExpired(selectedVRAPending.expirationDate)) {
      setDetailsError(VRA_ACTION_MESSAGES.EXPIRED.error);
      return;
    }
    const guaranteeExpiresAt = selectedVRAPending.guaranteePurchase?.expiresAt;
    if (isExpired(guaranteeExpiresAt)) {
      setExtensionDays(calculateExtensionDays(guaranteeExpiresAt));
      setIsExtensionConfirmOpen(true);
    } else {
      setIsConfirmModalOpen(true);
    }
  };

  const invalidatePendingVRA = () => {
    onActionHandler('invalidate', selectedVRAPending);
  };

  const onExtensionBackHandler = () => {
    setIsExtensionConfirmOpen(false);
    setExtensionDays(null);
  };

  const onExtensionProceedHandler = async() => {
    const { error: allowErrorMessage } = VRA_ACTION_MESSAGES.ALLOW;
    const guaranteePurchase = selectedVRAPending?.guaranteePurchase;
    setIsExtendingGuarantee(true);
    try {
      const { data: eligibilityData } = await client.query({
        query: guaranteeExtensionEligibilityQuery,
        variables: { guaranteePurchaseId: String(guaranteePurchase?.id) },
        fetchPolicy: 'network-only',
      });
      const eligibility = eligibilityData?.extensionEligibility;
      if (!eligibility) {
        throw new Error('Extension eligibility unavailable');
      }
      if (!eligibility.allowed) {
        setIsExtendingGuarantee(false);
        setFlashMessage(null);
        setError(eligibility.reason || VRA_ACTION_MESSAGES.EXPIRED.error);
        resetVRASelection();
        return;
      }

      const { data: { updateGuaranteePurchase } } = await submitUpdateGuaranteePurchaseMutation({
        variables: {
          purchaseInput: {
            guaranteePurchase: buildGuaranteePurchaseExtensionInput(selectedVRAPending, extensionDays),
          },
        },
      });
      setIsExtendingGuarantee(false);
      if (updateGuaranteePurchase.success) {
        submitPendingVRAAction(selectedVRAPending, 'ALLOW');
        resetVRASelection();
      } else {
        setFlashMessage(null);
        setError(allowErrorMessage);
        resetVRASelection();
      }
    } catch (e) {
      setIsExtendingGuarantee(false);
      setFlashMessage(null);
      setError(allowErrorMessage);
    }
  };

  return (
    <div>
      <Header>VRA Pending</Header>
      {flashMessage && (
        <Message
          onDismiss={() => setFlashMessage(null)}
          content={flashMessage}
          positive
        />
      )}
      {error && (
        <Message
          onDismiss={() => setError(null)}
          content={error}
          negative
        />
      )}
      <VRAPendingSearch
        onSubmit={onSearchSubmitHandler}
        searchFields={{}}
        searchOptions={['dealerNumber', 'vin', 'vra']}
      />
      <VRAPendingList
        VRAsPending={pendingReturnInvoices}
        actionHandler={onActionHandler}
        canEdit={canEdit}
      />
      {selectedVRAPending && isDetailsModalOpen && (
        <VRAPendingDetails
          pendingVRA={selectedVRAPending}
          isDetailsModalOpen={isDetailsModalOpen}
          onCloseHandler={resetVRASelection}
          allowPendingVRA={allowPendingVRA}
          errorMessage={detailsError}
          onErrorDismiss={() => setDetailsError(null)}
          invalidatePendingVRA={invalidatePendingVRA}
          notes={notes}
          notesLoading={notesLoading}
          notesError={notesError}
          canEdit={canEdit}
        />
      )}
      {selectedVRAPending && isInvalidateModalOpen && (
        <InvalidatePendingForm
          isInvalidateModalOpen={isInvalidateModalOpen}
          onCloseHandler={onInvalidateModalCancelHandler}
          onSubmitHandler={onInvalidateModalSubmitHandler}
        />
      )}
      {selectedVRAPending && isEditModalOpen && (
        <ReturnInvoiceEdit
          returnInvoice={selectedVRAPending}
          isEditModalOpen={isEditModalOpen}
          onCloseHandler={onEditCloseHandler}
          onSubmitHandler={onEditSubmitHandler}
          submitting={isEditSubmitting}
        />
      )}
      {selectedVRAPending && isNotesModalOpen && (
        <PendingVraNotesDialog
          vraId={selectedVRAPending.id}
          dealerName={selectedVRAPending.guaranteePurchase?.guaranteeOffer?.dealerName}
          notes={notes}
          notesLoading={notesLoading}
          notesError={notesError}
          submitting={isNoteSubmitting}
          errorMessage={noteSubmitError}
          onClose={resetVRASelection}
          onConfirm={onNoteSubmitHandler}
          canAddNote={canEdit}
        />
      )}
      {selectedVRAPending && isExtensionConfirmOpen && (
        <ConfirmGuaranteeExtension
          pendingVRA={selectedVRAPending}
          extensionDays={extensionDays}
          open={isExtensionConfirmOpen}
          onBackHandler={onExtensionBackHandler}
          onProceedHandler={onExtensionProceedHandler}
          submitting={isExtendingGuarantee}
        />
      )}
      <Confirm
        open={isConfirmModalOpen}
        content='Are you sure you want to allow this pending VRA?'
        onCancel={onConfirmModalCancelHandler}
        onConfirm={onConfirmModalConfirmHandler}
      />
    </div>
  );
};


const mapDispatchToProps = dispatch => ({
  setPageAction: () => dispatch(setPage({})),
});

VRAPending.propTypes = {
  data: PropTypes.shape({
    pendingReturnInvoices: PropTypes.array,
    refetch: PropTypes.func,
  }),
  pendingVRAMutation: PropTypes.func.isRequired,
  addPendingVraNoteMutation: PropTypes.func,
  updateGuaranteePurchaseMutation: PropTypes.func.isRequired,
  updateReturnInvoiceMutation: PropTypes.func.isRequired,
  setPageAction: PropTypes.func,
  user: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default compose(
  withRouter,
  withPagination(pendingReturnInvoicesQuery, 'pendingReturnInvoices'),
  graphql(pendingVRAMutation, { name: 'pendingVRAMutation' }),
  graphql(addPendingVraNoteMutation, { name: 'addPendingVraNoteMutation' }),
  graphql(updateGuaranteePurchaseMutation, { name: 'updateGuaranteePurchaseMutation' }),
  graphql(updateReturnInvoiceMutation, { name: 'updateReturnInvoiceMutation' }),
  LoadingDataEnhancer,
  connect(null, mapDispatchToProps)
)(VRAPending);
